import React, { useEffect, useRef } from "react";
import { useReceivablesComments } from "../../hooks/useReceivablesComments";
import { useTheme, Grid2 as Grid, Accordion, AccordionSummary, Typography, AccordionDetails, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useReceivablesAddComment } from "../../hooks/useReceivablesAddComment";
import InvoiceCommentsLoading from "./InvoiceCommentsLoading";
import { ActivityModel } from "@attelas/shared";
import dayjs from "dayjs";
import { ActivityRender } from "../../pages/Activities/ActivityRender";
import { MentionsInput } from "./MentionsInput";
import { getClosestScrollParent, scrollToBottom } from "../../utils/dom";

export interface InvoiceCommentsProps {
  height?: number | string;
  invoiceId?: string;
  title?: string;
  onCommentAdded?: () => void;
}

export default function InvoiceComments({
  height = "300px",
  invoiceId,
  title = "Comments",
  onCommentAdded,
}: InvoiceCommentsProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isLoading, comments, refetchComments } = useReceivablesComments(invoiceId);
  const { addComment } = useReceivablesAddComment(invoiceId || "", {
    onSuccess() {
      onCommentAdded?.();
      refetchComments().finally(() => {
        const scrollParent = getClosestScrollParent(containerRef.current);
        if (scrollParent) {
          scrollToBottom(scrollParent);
        }
      });
    },
  });

  const sortedActivities =
    comments?.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) ?? [];

  const scrollToEnd = () => {
    if (!scrollContainerRef.current) return;
    const scroller = scrollContainerRef.current;
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
  };

  const renderActivities = () => {
    if (!sortedActivities.length) {
      return (
        <Grid
          container={true}
          justifyContent="center"
          alignItems="center"
          flex={1}
          height="100%"
          sx={{ color: "text.secondary" }}
        >
          No comments yet.
        </Grid>
      );
    }

    return sortedActivities.map((activity, index) => (
      <ActivityRender
        key={activity.id || activity.createdAt}
        activity={activity}
        leading={computeLeading(sortedActivities, index)}
      />
    ));
  };

  useEffect(() => {
    scrollToEnd();
  }, [comments?.length]);

  if (isLoading) {
    return <InvoiceCommentsLoading />;
  }

  return (
    <Box>
      <Accordion ref={containerRef} defaultExpanded>
        <AccordionSummary
          expandIcon={<KeyboardArrowDownIcon />}
          aria-controls="information-panel-content"
          id="information-panel"
        >
          <Typography variant="h3">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container={true}
            flexDirection="column"
            flexWrap="nowrap"
            minHeight={150}
            maxHeight={height || "100%"}
            bgcolor={isDarkMode ? "#121212" : "common.white"}
            py={1}
            borderRadius={2}
          >
            <Grid
              ref={scrollContainerRef}
              container={true}
              flexDirection="column"
              flexWrap="nowrap"
              height="100%"
              flex="1 1 auto"
              pb={1}
              px={1.5}
              sx={{ overflowY: "auto" }}
            >
              {renderActivities()}
            </Grid>
            <Grid component="footer" container={true} width="100%" pt={1} px={1.5} spacing={1}>
              <Grid size={12}>
                <MentionsInput
                  addComment={async (params) => {
                    await addComment(params);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

function computeLeading(sortedActivities: ActivityModel[], index: number): boolean {
  if (index === 0) {
    return true;
  }

  const previousActivity = sortedActivities[index - 1];
  const currentActivity = sortedActivities[index];

  if (previousActivity.createdBy?.id !== currentActivity.createdBy?.id) {
    return true;
  }

  return Math.abs(dayjs(currentActivity.createdAt).diff(dayjs(previousActivity.createdAt), "minutes")) > 0;
}
